import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  AbstractControl,
  ValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { BoxService } from '@app/services/boxes/box.service';
import { VariantColumnsForMetafieldInput, VariantMetafieldMapping } from '../package.model';
import { ShopService } from '@app/services/shops/shop.service';
import { catchError } from 'rxjs/operators';

export interface PdqIqSettings {
  metafieldMapping: VariantMetafieldMapping;
}

@Component({
  selector: 'app-settings-pdq-iq',
  templateUrl: './pdq-iq.component.html',
  styleUrls: ['./pdq-iq.component.scss', '../../settings.scss'],
  standalone: false,
})
export class SettingsPdqIqComponent implements OnInit {
  @ViewChild('pdqIqContainer') private pdqIqContainer: ElementRef;
  pdqIqForm: UntypedFormGroup;
  isNew = true;
  fields: {
    name: string;
    value: string;
    variantField: VariantColumnsForMetafieldInput;
    dropdownOptions?: { label: string; value: string }[];
    dropdownPlaceholder?: string;
  }[] = [];
  isLoading = false;
  showFormErrors = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private boxService: BoxService,
    private shopService: ShopService,
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  initializeForm(): void {
    this.fields = [
      { name: 'Length', value: '', variantField: VariantColumnsForMetafieldInput.Length },
      { name: 'Width', value: '', variantField: VariantColumnsForMetafieldInput.Width },
      { name: 'Height', value: '', variantField: VariantColumnsForMetafieldInput.Height },
      // {
      //   name: 'Units',
      //   value: '',
      //   variantField: VariantColumnsForMetafieldInput.DistanceUnit,
      //   // dropdownOptions: [
      //   //   { label: 'Inch', value: 'in' },
      //   //   { label: 'Centimeter', value: 'cm' },
      //   // ],
      //   // dropdownPlaceholder: 'Fallback Units',
      // },
    ];

    const formControls = this.fields.reduce((controls, field, index) => {
      controls[`field${index}`] = ['', [Validators.required, this.namespaceKeyValidator()]];
      return controls;
    }, {});

    formControls['checkbox'] = [false];

    this.pdqIqForm = this.formBuilder.group(formControls);

    this.populateFormWithSavedValues();
  }

  savePdqIq(): void {
    try {
      this.isLoading = true;
      if (this.pdqIqForm.valid) {
        const variantMetafieldMapping = this.convertFormFields();
        if (Object.keys(variantMetafieldMapping).length > 0) {
          const pdqIqSettings: PdqIqSettings = {
            metafieldMapping: variantMetafieldMapping,
          };
          this.boxService
            .savePdqIqSettings(pdqIqSettings)
            .pipe(
              catchError((error) => {
                throw new Error(`BoxService error: ${error}`);
              }),
            )
            .subscribe((response) => {
              this.isLoading = false;
              this.settingsSaved();
            });
        } else {
          throw new Error('Missing required fields');
        }
      } else {
        throw new Error('Form is invalid');
      }
    } catch (error) {
      console.error('Error saving PDQ IQ settings', error);
      this.isLoading = false;
      this.showFormErrors = true;
    }
  }

  resetForm(): void {
    this.showFormErrors = false;
    this.pdqIqForm.reset();
    this.initializeForm();
  }

  navigateToSettings() {
    this.router.navigate(['./settings']);
  }

  navigateToPackages() {
    this.router.navigate(['./settings/packages']);
  }

  scrollToBottom() {
    setTimeout(() => {
      this.pdqIqContainer.nativeElement.scroll({
        top: this.pdqIqContainer.nativeElement.scrollHeight,
        left: 0,
        behavior: 'smooth',
      });
    }, 0);
  }

  settingsSaved() {
    this.shopService.refreshShop();
    this.navigateToPackages();
  }

  getFormErrors() {
    const errors = [];
    Object.keys(this.pdqIqForm.controls).forEach((key) => {
      const control = this.pdqIqForm.get(key);
      if (control && !control.valid && key.startsWith('field')) {
        const fieldName = this.fields[parseInt(key.replace('field', ''), 10)].name;
        errors.push(`${fieldName} is required and must be in the format "namespace.key"`);
      }
    });
    return errors;
  }

  convertFormFields(): VariantMetafieldMapping {
    const formValues = this.pdqIqForm.value;
    const variantMetafieldMapping: VariantMetafieldMapping = {};

    this.fields.forEach((field, index) => {
      const fieldValue = formValues[`field${index}`];
      if (fieldValue) {
        const [namespace, key] = fieldValue.split('.');
        if (!variantMetafieldMapping[namespace]) {
          variantMetafieldMapping[namespace] = {};
        }
        variantMetafieldMapping[namespace][key] = field.variantField;
      }
    });

    return variantMetafieldMapping;
  }

  namespaceKeyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value === 'string' && /^[^.]+\.[^.]+$/.test(value)) {
        return null; // Valid
      }
      return { invalidFormat: true }; // Invalid
    };
  }

  populateFormWithSavedValues(): void {
    this.boxService.getPdqIqSettings().subscribe((settings) => {
      if (settings) {
        const metafieldMapping = settings.metafieldMapping;

        this.fields.forEach((field, index) => {
          for (const namespace in metafieldMapping) {
            for (const key in metafieldMapping[namespace]) {
              if (metafieldMapping[namespace][key] === field.variantField) {
                this.pdqIqForm.controls[`field${index}`].setValue(`${namespace}.${key}`);
              }
            }
          }
        });
      }
    });
  }
}
